/**
 * The clickable rows inside the recurring-failure and open-task panels.
 *
 * Each row reports one record and opens its recorded facts. A task row also offers a way
 * through to the task itself, so a row holds two controls rather than being one.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Badge } from '@amzn/stencil-react-components/badge';
import { Chip, ChipBackgroundColor, ChipSize } from '@amzn/stencil-react-components/chip';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
import { ButtonCard, Card } from '@amzn/stencil-react-components/card';

import { CardChevron } from 'src/v2/shared/ui/CardChevron';
import { edgeLine } from './panelEdges';
import type {
  OpenTaskRef,
  RecurringFailureItem,
} from 'src/types/starlight/AuditInsights';
import {
  openTaskMeta,
  recurringMeta,
  type MetaFormatters,
} from '../logic/insightRowMeta';
import { taskStatusBadgeType, taskStatusLabel } from './taskStatus';

/**
 * Edge on a row.
 *
 * The card's default edge is `color.border.primary`, which is dark enough to read as a
 * heavy outline on every row of a list; a status-neutral edge separates the rows without
 * competing with them. Goes through `css` because a Card spreads its own `border` after the
 * props it was given, so the `border` prop is discarded.
 *
 * Hoisted so emotion sees one stable object rather than a new one each render.
 */
const ROW_EDGE = edgeLine('color.status.neutral-border');

/**
 * Strips the trigger's own card chrome.
 *
 * `ButtonCard` is the right primitive for a large clickable region — it is a real button
 * and carries no link styling — but it is built to BE a card, so on its own it draws a
 * bordered, shadowed, hover-scaling box. Here it sits inside the row's card so the row can
 * also hold an action beside it, and a second box inside the first reads as the row itself,
 * which puts anything outside it seemingly outside the row. So it gives up every piece of
 * card styling and inherits the row's.
 *
 * Only the pieces that cannot be reached by prop are here. `backgroundColor` and `padding`
 * go through props at the call site, because the Card reads those before it builds its own
 * styles. `border`, `boxShadow` and the hover and press transforms cannot: the Card spreads
 * its own `borderStyles`, `elevatedStyles` and `interactiveStyles` after the styles it was
 * given, so a prop for any of them is discarded. There is no prop that turns that chrome
 * off — `isElevated` does nothing once a card is `interactive`.
 */
/** Cancels the lift a ButtonCard applies on hover and on press. */
const NO_LIFT = { transform: 'none', boxShadow: 'none' } as const;

const ROW_TRIGGER = {
  'border': 'none',
  'boxShadow': 'none',
  'width': '100%',
  'textAlign': 'left',
  ':hover': NO_LIFT,
  ':active': NO_LIFT,
} as const;

/**
 * The id naming a row, in front of its title.
 *
 * One component for both row kinds, so a question id and a task id cannot drift apart in
 * size or shape — the size is fixed here rather than restated at each call site. Only the
 * tint is a caller's choice, because it carries meaning: a recurring failure's id is red
 * because those rows *are* failures, where a task id is neutral because an id means the
 * same thing whatever state the task is in.
 */
function IdChip({
  label,
  backgroundColor,
}: {
  label: string;
  backgroundColor: ChipBackgroundColor;
}): JSX.Element {
  return (
    <Chip size={ChipSize.ExtraSmall} backgroundColor={backgroundColor}>
      {label}
    </Chip>
  );
}

/** A question that failed here and had failed before. */
export function RecurringFailureRow({
  item,
  formatters,
  onOpen,
}: {
  item: RecurringFailureItem;
  formatters: MetaFormatters;
  /** Opens the row's recorded facts. */
  onOpen: () => void;
}): JSX.Element {
  return (
    <MetaRow
      titleLeading={
        <IdChip
          label={formatters.f('questionIdChip', { id: item.questionId })}
          backgroundColor={ChipBackgroundColor.Red}
        />
      }
      // The title is optional on the model; the id already names the question.
      title={item.questionTitle}
      meta={recurringMeta(item, formatters)}
      onOpen={onOpen}
      // A question has no page of its own, so the row only leads to its facts — and with
      // nothing to compete with, the chevron belongs inside the trigger it describes.
      affordance={<CardChevron />}
    />
  );
}

/** A task raised by an earlier audit that is still outstanding. */
export function OpenTaskRow({
  task,
  formatters,
  onOpen,
  viewHref,
  viewLabel,
}: {
  task: OpenTaskRef;
  formatters: MetaFormatters;
  /** Opens the task's recorded facts. */
  onOpen: () => void;
  /** The task's own page. */
  viewHref: string;
  viewLabel: string;
}): JSX.Element {
  const navigate = useNavigate();
  const handleView = useCallback(() => {
    void navigate(viewHref);
  }, [navigate, viewHref]);

  return (
    <MetaRow
      // The task's own id, in the slot and the component a recurring row gives its question
      // id. Always the default tint: an id means the same thing whatever state the task is
      // in, so colouring it by status would say the same thing as the badge a second time.
      titleLeading={
        <IdChip
          label={formatters.f('taskIdChip', { id: task.taskId })}
          backgroundColor={ChipBackgroundColor.Default}
        />
      }
      title={task.title ?? task.linkedQuestionTitle}
      // Beside the name, as the prototype has it: the status qualifies the task rather
      // than labelling the row.
      titleTrailing={
        // The Stencil Badge the audit and task lists already render, so one status looks
        // the same on every surface that shows it.
        <Badge
          type={taskStatusBadgeType(task.taskStatus)}
          value={taskStatusLabel(task.taskStatus)}
        />
      }
      meta={openTaskMeta(task, formatters)}
      onOpen={onOpen}
      action={
        // A sibling of the trigger, not a child of it: one button inside another is
        // invalid, and one control cannot mean two things. This row carries no chevron
        // for that reason — the affordance would sit next to a control that is not it.
        <Button
          variant={ButtonVariant.Secondary}
          size={ButtonSize.Small}
          onClick={handleView}
        >
          {viewLabel}
        </Button>
      }
    />
  );
}

/**
 * Shared row shape: a trigger holding the facts, and at most one control beside it.
 *
 * The trigger is a real `button`, so opening a row works from the keyboard and announces
 * itself — the prototype's onclick div does neither. It sits inside the card rather than
 * being the card, so a row can also carry an action without nesting one control in another.
 *
 * The two trailing slots are not interchangeable. An `affordance` only describes the
 * trigger, so it goes inside it and a reader aiming at it opens the row. An `action` does
 * something else, so it has to stay outside, and a row carrying one has no affordance to
 * avoid placing that cue beside a control it does not belong to.
 *
 * `meta` is always rendered. Both builders emit at least one fact — the streak for a
 * recurring failure, the linked question for a task — so there is no empty case.
 */
function MetaRow({
  titleLeading,
  title,
  titleTrailing,
  meta,
  affordance,
  action,
  onOpen,
}: {
  titleLeading?: JSX.Element;
  title: string | undefined;
  titleTrailing?: JSX.Element;
  meta: string;
  /** Decoration describing the trigger, rendered within it. */
  affordance?: JSX.Element;
  /** A control of its own, rendered beside the trigger. */
  action?: JSX.Element;
  onOpen: () => void;
}): JSX.Element {
  return (
    <Card padding="dimensions.spacing.200" css={ROW_EDGE}>
      <Row gridGap="dimensions.spacing.200" alignItems="center" minWidth={0} width="100%">
        <Col flex={1} minWidth={0}>
          <ButtonCard
            type="button"
            onClick={onOpen}
            backgroundColor="transparent"
            padding="dimensions.spacing.none"
            css={ROW_TRIGGER}
          >
            <Row
              gridGap="dimensions.spacing.200"
              alignItems="center"
              width="100%"
              minWidth={0}
            >
              <Col
                flex={1}
                gridGap="dimensions.spacing.50"
                minWidth={0}
                alignItems="flex-start"
              >
                <TitleLine
                  leading={titleLeading}
                  title={title}
                  trailing={titleTrailing}
                />
                <Text fontSize="T100" color="color.text.secondary">
                  {meta}
                </Text>
              </Col>
              {affordance}
            </Row>
          </ButtonCard>
        </Col>
        {action}
      </Row>
    </Card>
  );
}

/**
 * The record's id, its name, and the status qualifying it, on one line.
 *
 * One line rather than a stack so the id reads as a label on the name instead of a marker
 * in the row's corner. Wraps rather than truncating when a long title runs out of room.
 */
function TitleLine({
  leading,
  title,
  trailing,
}: {
  leading: JSX.Element | undefined;
  title: string | undefined;
  trailing: JSX.Element | undefined;
}): JSX.Element {
  return (
    <Row gridGap="dimensions.spacing.100" alignItems="center" flexWrap="wrap">
      {leading}
      {title ? <Text fontSize="T200">{title}</Text> : null}
      {trailing}
    </Row>
  );
}
